// src/utilities/credentialConsumers.js — SOUND-709 (Track A step 5)
//
// Who is using a credential, and what breaks if it goes away.
//
// This is the part of the UI that has to exist before revoke does. Gateway
// 1310948 serves 6 MIDs across 5 processors on ONE credential — deactivating it
// without showing that takes down five MIDs nobody was looking at. Silence here
// is not a missing feature, it is an outage waiting for a click.

/**
 * Flatten the consumers of one credential into a single list.
 * Edges (MIDs) and pointers (integrations, pipeline configs) are different
 * mechanisms — cardinality decided that, SOUND-707 — but a human revoking
 * something does not care which is which. They care what stops working.
 */
export function summarizeConsumers({ midLinks = [], integrations = [], pipelineConfigs = [] } = {}) {
  const rows = [
    // Same identity as an integration-linked MID — CORP leads, then
    // DBA · processor · number. An edge carries only a cached label, so
    // otherLabel is the fallback rather than the answer.
    ...midLinks.map((l) => ({
      type: "mid",
      id: l.otherId,
      label: l.corpName || l.otherLabel || "Unknown company",
      detail: identity([l.dbaName, l.processorName, l.midNumber]) || l.otherLabel || "",
      corpName: l.corpName || null,
      dbaName: l.dbaName || null,
      processorName: l.processorName || null,
      midNumber: l.midNumber || null,
      via: "edge",
    })),
    // SOUND-707 — a MID's identity is CORP + DBA + PROCESSOR + MID NUMBER.
    //
    // Two earlier attempts were wrong for the same underlying reason: they used
    // something that is not unique. The gateway id is shared by every MID on
    // that gateway, so rows rendered identically. The descriptor is reused
    // across MIDs, so rows could still read the same and mean different things.
    // Only (mid number, processor) is unique — SOUND-656's key.
    //
    // DBA is frequently absent (0 of 6 in DEV, 144 of 398 in prod) and is left
    // out rather than substituted from the descriptor. A descriptor standing in
    // for a DBA is the quiet substitution SOUND-654 exists to prevent.
    ...integrations.map((i) => ({
      type: "integration",
      id: i._id,
      // WHO owns the consuming MID — often not the credential's owner.
      label: i.corpName || "Unknown company",
      // WHAT it is: the parts that actually identify it, in order of strength.
      detail: identity([i.dbaName, i.processorName, i.midNumber]),
      gateway: i.externalMerchantId || null,
      corpName: i.corpName || null,
      dbaName: i.dbaName || null,
      processorName: i.processorName || null,
      midNumber: i.midNumber || null,
      via: "pointer",
    })),
    ...pipelineConfigs.map((c) => ({
      type: "pipeline", id: c._id, label: `${c.slug}/${c.integrationName}`, via: "pointer",
    })),
  ];
  return { rows, total: rows.length, byType: countBy(rows, "type") };
}

/**
 * Join the identity parts, dropping anything that is not a printable scalar.
 *
 * A `[object Object]` reached the UI when a lookup map held whole documents and
 * one call site forgot to read `.name` off it. The server bug is fixed, but the
 * renderer should not be the thing that turns a shape error into confident
 * nonsense — an omitted part is legible, a stringified object is not.
 */
function identity(parts) {
  return parts.filter((p) => typeof p === "string" && p.trim()).join(" · ");
}

function countBy(rows, key) {
  return rows.reduce((a, r) => ({ ...a, [r[key]]: (a[r[key]] || 0) + 1 }), {});
}

/**
 * What a human must be told BEFORE deactivating or deleting.
 *
 * `severity` is deliberately driven by consumer COUNT, not by whether the
 * credential looks important. The dangerous case is the one that looks routine:
 * a single portal login quietly serving six MIDs.
 */
export function revokeImpact(summary) {
  const total = summary?.total ?? 0;
  if (total === 0) {
    return { severity: "none", total, message: "Nothing is using this credential." };
  }
  const parts = Object.entries(summary.byType)
    .map(([t, n]) => `${n} ${t}${n === 1 ? "" : "s"}`)
    .join(", ");
  return {
    severity: total >= 3 ? "high" : "low",
    total,
    message:
      total === 1
        ? `1 consumer will lose access: ${parts}.`
        : `${total} consumers will lose access: ${parts}. They will stop working immediately.`,
  };
}

/**
 * Credentials shared across MORE THAN ONE company's MIDs.
 *
 * In prod, 110 of 110 midIntegration pointers reference a credential owned by a
 * DIFFERENT company — sharing is the NORMAL shape of NMI, not an edge case. A
 * UI that presents every credential as belonging to one merchant is lying about
 * the data, and that lie is what made "copy it to the other MID" sound sensible.
 */
export function isSharedAcrossCompanies(credential, consumerCompanyIds = []) {
  const others = consumerCompanyIds.filter((id) => id && id !== credential?.companyId);
  return { shared: others.length > 0, otherCompanyCount: new Set(others).size };
}
